import React from 'react';
import immutable from 'immutable';
import DateSearchField from './DateSearchField';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import SearchFieldsStore from 'stores/search/SearchFieldsStore';

const dsfo = ConstValues.datetimeSearchFieldsOptions;
const dsvt = ConstValues.datetimeSearchValueTypes;
const dwsf = ConstValues.datetimeWithinSearchFieldsOptions;

/**
 * A `DateSearchWidgets` renders all the `DateSearchField` according to the
 * `field_schema` objects returned in the `fields` field from a `/api/v1/search`
 * endpoint. Notice that all `fields_schema` objects which are date search are
 * grouped together into one sidebar section
 */
class DateSearchWidgets extends React.Component {
  /**
   * Return the value of the top selected value in its <Select /> for the searchField `sf`
   * @param  {records.Field} sf A field schema
   * @return {object}    An integer value
   */
  getDateSearchFieldTopSelectedValue(sf) {
    const state = this.props.searchFieldsState.get(sf.name);
    if (state) {
      return state.get('selectedValue');
    } else {
      return null;
    }
  }

  /**
   * Return the selected value for the within-date <Select /> of the searchField `sf`
   * @param  {records.Field} sf A field schema
   * @return {string}     A `ConstValues.datetimeWithinSearchFieldsOptions` string value
   */
  getDateSearchFieldSelectedDateWithinValue(sf) {
    const state = this.props.searchFieldsState.get(sf.name);
    if (state) {
      return state.get('selectedWithinDateValue');
    } else {
      return dwsf.withinTheLastSevenDays;
    }
  }

  /**
   * Return the current date for a search field `sf`
   * @param  {SearchField} sf A SearchField
   * @return {Date}    The current date picked by the user or null
   */
  getDatetimeSearchFieldDate(sf) {
    // This is kept in the store here. Not sure why it's not in the state from props.
    return SearchFieldsStore.getState().getIn(['searchExpression', sf.name]);
  }

  /**
   * Call `SearchFieldsActions.updateSearchFieldsState` to update the
   * `SearchFieldsStore.searchFieldsState`
   * @param {records/Field} searchField A records/Field object
   * @param  {string} newValue A ConstValues.datetimeSearchFieldsOptions string
   */
  onSelectOptionChange = (searchField, newValue) => {
    const state = immutable.Map({
      name: searchField.name,
      type: searchField.type,
      selectedValue: newValue,
    });
    if (newValue === dsfo.within) {
      const defaultWithinLastNumberOfDays = ConstValues.defaultWithinSearchFieldsOption;
      state.selectedWithinDateValue = defaultWithinLastNumberOfDays;
      const dt = new Date();
      dt.setDate(dt.getDate() - defaultWithinLastNumberOfDays);
      SearchFieldsActions.updateDatetimeSearchField(searchField, dt, dsvt.within);
    } else if (newValue === '') {
      // User has unchecked the "Select period" dropdown. We should remove the "Date" search
      // restriction from the search request body and initiates a search request to update the results
      SearchFieldsActions.updateDatetimeSearchExpression(searchField, false);
    }
    setImmediate(() => {
      SearchFieldsActions.updateSearchFieldsState({
        state: state,
      });
    });
  };

  /**
   * Update `SearchFieldsStore.getState().get('searchFieldsState')` using `searchField`
   * and `newValue`
   * @param  {records/Field} searchField A searchField object
   * @param  {string} selectedOption A ConstValues.datetimeSearchFieldsOptions string
   * @param {string} newValue A string that maps to the selected value of `searchField`
   */
  onSelectedWithinDatetimeChange = (searchField, selectedOption, newValue) => {
    const state = immutable.Map({
      name: searchField.name,
      type: searchField.type,
      selectedValue: selectedOption,
      selectedWithinDateValue: newValue,
    });
    setImmediate(() => {
      SearchFieldsActions.updateSearchFieldsState({
        state: state,
      });
    });
  };

  /**
   * Remove `searchField` from `SearchFieldsStore.getState().get('searchExpression')`
   * @param  {records/Field} searchField A `searchField` to be removed from the search
   *                                     expression
   */
  removeDatetimeSearchExpression = searchField => {
    SearchFieldsActions.removeSearchField(searchField);
  };

  /**
   * Return the `toggled` state value for a `searchField`
   * @param  {immutable/Record} searchField A SearchField record
   * @return {Boolean}             true if the searchField is toggled and false if not
   */
  getDatetimeSearchFieldsToggled(searchField) {
    if (this.props.searchFieldsToggled) {
      return this.props.searchFieldsToggled.get(searchField.name);
    }
    return false;
  }

  /**
   * Render a date search field for the provided search field.
   * @param {searchField} sf The search field to render.
   * @return {JSX} A date search widget.
   */
  renderComponent(sf) {
    return (
      <DateSearchField
        key={sf.name}
        searchField={sf}
        dateValue={this.getDatetimeSearchFieldDate(sf)}
        toggled={this.getDatetimeSearchFieldsToggled(sf)}
        value={this.getDateSearchFieldTopSelectedValue(sf)}
        dateWithinValue={this.getDateSearchFieldSelectedDateWithinValue(sf)}
        onSelectOptionChange={this.onSelectOptionChange}
        onDatetimeChange={this.onDatetimeChange}
        removeDatetimeSearchExpression={this.removeDatetimeSearchExpression}
        updateLastToggledOnSearchWidget={this.props.updateLastToggledOnSearchWidget}
        shouldFocus={
          this.props.shouldRenderToggled &&
          this.getDatetimeSearchFieldsToggled(sf) &&
          sf.name === this.props.lastToggledOnSearchWidget
        }
        shouldSortByToggled={this.props.shouldSortByToggled}
        onSelectedWithinDatetimeChange={this.onSelectedWithinDatetimeChange}
      />
    );
  }

  /**
   * Returns a boolean that indicates whether the Search Field should not be rendered.
   * @return {boolean} When true indicates that the Search Field should not be rendered.
   */
  shouldNotRenderComponent(sf) {
    return !(
      (this.props.shouldRenderToggled && this.getDatetimeSearchFieldsToggled(sf)) ||
      (this.props.shouldRenderNotToggled && !this.getDatetimeSearchFieldsToggled(sf))
    );
  }

  render() {
    const searchFields = this.props.searchFields.sort(sf => sf.order);
    return (
      <div className="search-widget">
        {searchFields
          .filterNot(this.shouldNotRenderComponent, this)
          .map(this.renderComponent, this)
          .toArray()}
      </div>
    );
  }

  /**
   * Trigger `SearchFieldsActions.updateDatetimeSearchField` to update
   * `SearchStore.getState().get('searchExpression')` with `searchField` and
   * its `toDatetime`
   * @param  {records/Field} searchField A search field
   * @param  {Date} datetime A `Date` object
   * @param {string} type A `ConstValues.datetimeSearchValueTypes` string value
   */
  onDatetimeChange = (searchField, datetime, type) => {
    SearchFieldsActions.updateDatetimeSearchField(searchField, datetime, type);
  };
}

export default DateSearchWidgets;
